// Single source of truth for Side Panel width thresholds.
//
// Kept in its own dependency-free module so that both the panel itself (SidePanel.tsx)
// and the layout shell (Layout.tsx) can read the same numbers without creating an import
// cycle, and so the CSS breakpoint can never silently drift from the JS logic.

export const PANEL_SIZING = {
  defaultWidthRatio: 0.4,
  minWidth: 450,
  minVisibleMainContentRatio: 0.2,
  maxWidthRatio: 0.5,
} as const;

// The composer needs this much width before its placeholder/query text starts colliding
// with the mic, send and voice controls.
export const SIDE_PANEL_MIN_WIDTH = PANEL_SIZING.minWidth;

// Smallest container that can host the panel at its minimum width without breaking the
// "panel never exceeds maxWidthRatio of the container" rule. Below this the panel cannot
// be shown honestly, so callers must fall back to the modal instead of squeezing it in.
export const SIDE_PANEL_MIN_CONTAINER_WIDTH = Math.ceil(
  PANEL_SIZING.minWidth / PANEL_SIZING.maxWidthRatio
);

// How far past the minimum width a drag may travel before we hand off to the modal. Acts
// as hysteresis so a jittery pointer resting near the minimum doesn't flap.
export const MODAL_FALLBACK_DRAG_BUFFER = 60;

// Duration of the open/close animation: the panel slides in from the right edge while the
// main content area is squeezed by exactly the same amount, in the same frame budget.
// Shared so the panel can stay mounted for the whole collapse (see SidePanel.tsx) instead
// of vanishing before its container has finished animating shut.
export const SIDE_PANEL_TRANSITION_MS = 240;

// Ease-out: fast at the start so the panel feels responsive to the click, settling gently
// at the end so the reflow of the main content doesn't read as a snap.
export const SIDE_PANEL_TRANSITION_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
